import { getLocalTimeZone, today } from '@internationalized/date';
import { maybe, type Maybe } from '@passionware/monads';
import { z } from 'zod';
import { ApiClient } from 'lib/api/ApiClient.types';
import {
  convertCustomFieldFiltersToHttp,
  FieldNameTypeMapping
} from 'v5/core/custom-fields/custom-fields-filters.http-converters';
import { convertCoreFieldFiltersToHttp } from 'v5/core/core-fields/core-fields-filters.http-converters';
import { mergeWorkspaceMembershipScopingIntoHttpFilters } from 'v5/core/app-scope/query/mergeWorkspaceScopingIntoHttpFilters';
import {
  convertEnumFilterToApi,
  createSingleFieldDateRangeFilterConverter
} from 'v5/platform/filters/filterConverters';
import { WithServices } from 'v5/platform/ts/services';
import { WithMetastructureMappingService } from 'v5/services/internal/MetastructureMappingService/MetastructureMappingService';
import { rowLevelValidation } from 'v5/platform/row-level-validation';
import {
  bookingConfirmationPreview$,
  bookingSearchListResponse$,
  booking$,
  createInvoiceItemToApi$,
  createReceiptItemToApi$,
  documentResponse$,
  updateInvoicePayloadToApi$,
  updateReceiptPayloadToApi$
} from './booking.api.http.schema';
import { Booking, Expense } from '../../../__types__';
import {
  bookingExpensesUpdateToWire,
  bookingResourcingUpdateToWire,
  expenseLineCommittedUpdateToWire
} from 'v5/api/resourcing/adapters/mutationToWire';
import {
  toWireBookingExpensesPatch,
  toWireBookingMutationPatch,
  toWireLegacyBookingPatch,
  toWireExpenseLinePatch
} from 'v5/api/resourcing/resourcingMoney.http';
import type { ExpenseLineUpdatePatch } from 'v5/api/resourcing/resourcing.mutation.types';
import {
  asGlobalBookingQuery,
  BookingApi,
  BookingConfirmationPreviewRequest,
  BookingConfirmationRequest,
  BookingListQuery,
  BookingQuery,
  BookingScopedQuery,
  MissingBookingConfirmationTemplateError
} from './booking.api';
import {
  scheduledFilter,
  ScheduledFilterPreset
} from 'v5/core/standard-date-filter/scheduled-filter';
import { convertApiBookingToBooking } from './booking.api.http.converter';
import { parseWithDecoratedError } from 'v4/entities/common/parseWithDecoratedError';
import { searchApiListEnvelopeUnknownResultsFromHttp$ } from 'v5/api/_common/query/pagination.http.schema';

/** Legacy association GET endpoints return `{ results, paging? }`, not a bare array. */
export function legacyAssociationListFromHttp<T>(response: unknown): T[] {
  if (Array.isArray(response)) {
    return response as T[];
  }
  const parsed =
    searchApiListEnvelopeUnknownResultsFromHttp$.safeParse(response);
  return parsed.success ? (parsed.data.results as T[]) : [];
}

// Map frontend sort fields to backend field names for bookings
const mapBookingSortFieldToApi = (field: string): string => {
  const fieldMap: Record<string, string> = {
    createdAt: 'created_at',
    updatedAt: 'updated_at',
    contact: 'contact_name',
    booking_type: 'booking_type_id',
    start_date: 'booking_start_date',
    end_date: 'booking_end_date',
    status_type: 'status_type'
    // Add more fields as needed based on Booking model
  };
  return fieldMap[field] ?? 'created_at';
};

// Convert sorter to API format
const convertBookingSorterToApi = (sorter: {
  field: string;
  order: 'asc' | 'desc';
}) => ({
  field: mapBookingSortFieldToApi(sorter.field),
  direction: sorter.order.toUpperCase() as 'ASC' | 'DESC'
});

// Create date range filter converter for booking dates (booking_start_date and booking_end_date)
const convertBookingDateRangeFilter =
  createSingleFieldDateRangeFilterConverter<ScheduledFilterPreset>({
    resolvePreset: preset =>
      scheduledFilter.resolveRange(preset, today(getLocalTimeZone()))
  });

// Convert additional booking filters (not core fields) to API format
function convertAdditionalBookingFiltersToApi(
  filters: BookingQuery['filters']
): Record<string, unknown> {
  const apiFilters: Record<string, unknown> = {};

  if (!filters) {
    return apiFilters;
  }

  // Status filter - sends as { status_type: { eq: [...] } }
  if (maybe.isPresent(filters.status_type)) {
    const statusTypeFilter = convertEnumFilterToApi(filters.status_type);
    if (statusTypeFilter) {
      apiFilters.status_type = statusTypeFilter;
    }
  }

  // Status ID filter - uses standard enum filter conversion
  if (maybe.isPresent(filters.status_id)) {
    const statusIdFilter = convertEnumFilterToApi(filters.status_id);
    if (statusIdFilter) {
      apiFilters.status_id = statusIdFilter;
    }
  }

  // Booking type ID filter
  if (maybe.isPresent(filters.booking_type_id)) {
    const bookingTypeFilter = convertEnumFilterToApi(filters.booking_type_id);
    if (bookingTypeFilter) {
      apiFilters.booking_type_id = bookingTypeFilter;
    }
  }

  // Contact ID filter
  if (maybe.isPresent(filters.contact_id)) {
    const contactFilter = convertEnumFilterToApi(filters.contact_id);
    if (contactFilter) {
      apiFilters.contact_id = contactFilter;
    }
  }

  // Booking date range filter - uses createDateRangeFilterConverter
  if (maybe.isPresent(filters.booking_date_range)) {
    const dateRangeFilter = convertBookingDateRangeFilter(
      filters.booking_date_range
    );
    if (dateRangeFilter) {
      apiFilters.booking_date_range = dateRangeFilter;
    }
  }

  return apiFilters;
}

export function getBookingListRequest(
  query: BookingListQuery,
  fieldMappings: FieldNameTypeMapping
): Record<string, unknown> {
  const scopedQuery: BookingScopedQuery =
    'workspaceId' in query ? query : asGlobalBookingQuery(query);
  const filters = {
    // Convert core field filters
    ...convertCoreFieldFiltersToHttp(scopedQuery.filters),
    // Convert additional booking-specific filters
    ...convertAdditionalBookingFiltersToApi(scopedQuery.filters),
    // CF filter lens: browsing scope (null = org/global values).
    ...convertCustomFieldFiltersToHttp(
      fieldMappings,
      scopedQuery.filters,
      scopedQuery.workspaceId
    ),
    ...mergeWorkspaceMembershipScopingIntoHttpFilters(scopedQuery)
  };

  return {
    query: scopedQuery.search,
    filters,
    p: scopedQuery.page.page + 1, // API uses 1-based pagination
    count: scopedQuery.page.pageSize,
    // Note: 'archived' is at root level, not in filters, per backend API requirements
    archived: scopedQuery.filters?.archived?.value ? true : false,
    // Sorting support
    ...(scopedQuery.sort
      ? {
          order_by: {
            ...convertBookingSorterToApi(scopedQuery.sort),
            enterprise_workspace_id: scopedQuery.workspaceId
          }
        }
      : {})
  };
}

function rethrowMissingBookingConfirmationTemplate(error: unknown): never {
  if ((error as Maybe<{ status?: number }>)?.status === 400) {
    throw new MissingBookingConfirmationTemplateError();
  }
  throw error;
}

/**
 * Singular booking confirmation endpoint — supports preview and send.
 * A 400 means the resolved scope has no template configured — the send dialog renders
 * that as a "configure a template" state rather than a generic failure.
 */
async function postBookingConfirmation(
  client: ApiClient,
  { bookingId, enterpriseWorkspaceId }: BookingConfirmationPreviewRequest,
  previewOnly: boolean
): Promise<unknown> {
  try {
    return await client.post(
      `/bookings/${bookingId}/send_booking_confirmation`,
      {
        data: {
          ...(previewOnly ? { previewOnly: true } : {}),
          ...(maybe.isPresent(enterpriseWorkspaceId)
            ? { enterpriseWorkspaceId }
            : {})
        },
        token: true
      }
    );
  } catch (error) {
    rethrowMissingBookingConfirmationTemplate(error);
  }
}

/**
 * Production bulk confirmation send (no preview). Same template-lens body field.
 */
async function postProductionBookingConfirmations(
  client: ApiClient,
  {
    productionId,
    enterpriseWorkspaceId
  }: Extract<BookingConfirmationRequest, { productionId: number }>
): Promise<unknown> {
  try {
    return await client.post(
      `/productions/${productionId}/send_booking_confirmation`,
      {
        data: {
          ...(maybe.isPresent(enterpriseWorkspaceId)
            ? { enterpriseWorkspaceId }
            : {})
        },
        token: true
      }
    );
  } catch (error) {
    rethrowMissingBookingConfirmationTemplate(error);
  }
}

export function createBookingApi(
  config: { client: ApiClient } & WithServices<
    [WithMetastructureMappingService]
  >
): BookingApi {
  const { client, services } = config;
  return {
    createItem: async data => {
      const raw = await client.post('/bookings', {
        data,
        token: true
      });
      const parsed = parseWithDecoratedError(booking$, raw);
      return convertApiBookingToBooking(parsed);
    },

    getList: async (query: BookingListQuery, signal) => {
      // Get field type mappings for custom fields
      const fieldMappings = Object.keys(query.filters).some(key =>
        key.startsWith('custom_')
      )
        ? await services.metastructureMappingService.getAllFieldNameTypeMappings()
        : // if no custom fields, don't use field mappings (important if we are in gues mode sorry
          {};

      const requestData = getBookingListRequest(query, fieldMappings);

      const response = await client.post('/search/bookings', {
        data: requestData,
        token: true,
        signal
      });

      const parsed = parseWithDecoratedError(
        bookingSearchListResponse$,
        response
      );

      return {
        items: rowLevelValidation.unsafeMapAll(
          parsed.results,
          convertApiBookingToBooking
        ),
        count: parsed.paging.total
      };
    },

    getItem: async id => {
      const response = await client.get<Booking>(`/bookings/${id}`, {
        token: true
      });
      const parsed = parseWithDecoratedError(booking$, response);
      return convertApiBookingToBooking(parsed);
    },

    getItems: async ids => {
      if (ids.length === 0) {
        return [];
      }

      const response = await client.post('/batch/bookings/search', {
        data: { ids },
        token: true
      });

      const parsed = parseWithDecoratedError(
        z.object({ data: z.array(booking$) }),
        response
      );
      return parsed.data.map(convertApiBookingToBooking);
    },

    updateItem: async (id, data) => {
      return client.put<Booking>(`/bookings/${id}`, {
        data: toWireLegacyBookingPatch(data),
        token: true
      });
    },

    updateResourcingBooking: async (id, patch) => {
      return client.put<Booking>(`/bookings/${id}`, {
        data: toWireBookingMutationPatch(bookingResourcingUpdateToWire(patch)),
        token: true
      });
    },

    updateResourcingBookingExpenses: async (id, patch) => {
      return client.put<Booking>(`/bookings/${id}`, {
        data: toWireBookingExpensesPatch(bookingExpensesUpdateToWire(patch)),
        token: true
      });
    },

    getBookingExpenses: async bookingIds => {
      if (bookingIds.length === 0) return [];
      const response = await client.get(
        `/bookings/${bookingIds.join(',')}/associations/expenses`,
        { token: true }
      );
      const list = legacyAssociationListFromHttp<Expense>(response);
      return bookingIds.map(bookingId => ({
        booking_id: bookingId,
        expenses: list.filter(
          expense => expense.booking_id === bookingId && !expense.archived
        )
      }));
    },

    updateExpense: async (expenseId, data) => {
      return client.put(`/expenses/${expenseId}`, {
        data: toWireExpenseLinePatch(data as ExpenseLineUpdatePatch),
        token: true
      });
    },

    updateResourcingExpenseLine: async (expenseId, patch) => {
      return client.put(`/expenses/${expenseId}`, {
        data: toWireExpenseLinePatch(expenseLineCommittedUpdateToWire(patch)),
        token: true
      });
    },

    previewBookingConfirmation: async request =>
      parseWithDecoratedError(
        bookingConfirmationPreview$,
        await postBookingConfirmation(client, request, true)
      ),

    sendBookingConfirmations: async (request: BookingConfirmationRequest) => {
      if ('productionId' in request) {
        await postProductionBookingConfirmations(client, request);
        return;
      }
      await postBookingConfirmation(client, request, false);
    },

    bulkArchive: async request => {
      await Promise.all(
        request.ids.map(id =>
          client.put(`/bookings/${id}`, {
            data: {
              archived: true
            },
            token: true
          })
        )
      );
    },

    bulkUnarchive: async request => {
      await Promise.all(
        request.ids.map(id =>
          client.put(`/bookings/${id}`, {
            data: { archived: false },
            token: true
          })
        )
      );
    },

    createInvoice: async (bookingId, payload) => {
      const body = createInvoiceItemToApi$.parse({
        document_data: payload.document_data,
        file: payload.file,
        status: payload.status,
        url: payload.url
      });
      const raw = await client.post(
        `/bookings/${bookingId}/associations/invoices`,
        { data: body, token: true }
      );
      return parseWithDecoratedError(documentResponse$, raw);
    },

    updateInvoice: async (bookingId, invoiceId, payload) => {
      const dataToSend = updateInvoicePayloadToApi$.parse(payload);
      const raw = await client.put(
        `/bookings/${bookingId}/associations/invoices/${invoiceId}`,
        { data: dataToSend, token: true }
      );
      return parseWithDecoratedError(documentResponse$, raw);
    },

    deleteInvoice: async (bookingId, invoiceId) => {
      return client.del(
        `/bookings/${bookingId}/associations/invoices/${invoiceId}`,
        { data: {}, token: true }
      );
    },

    createReceipt: async (expenseId, payload) => {
      const body = createReceiptItemToApi$.parse({
        document_data: payload.document_data,
        file: payload.file,
        status: payload.status
      });
      const raw = await client.post(
        `/expenses/${expenseId}/associations/receipts`,
        { data: body, token: true }
      );
      return parseWithDecoratedError(documentResponse$, raw);
    },

    updateReceipt: async (expenseId, receiptId, payload) => {
      const dataToSend = updateReceiptPayloadToApi$.parse(payload);
      const raw = await client.put(
        `/expenses/${expenseId}/associations/receipts/${receiptId}`,
        { data: dataToSend, token: true }
      );
      return parseWithDecoratedError(documentResponse$, raw);
    },

    deleteReceipt: async (expenseId, receiptId) => {
      return client.del(
        `/expenses/${expenseId}/associations/receipts/${receiptId}`,
        { data: {}, token: true }
      );
    }
  };
}
